import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BehaviorSubject, Subject, Observable } from 'rxjs';

import {
  IVoiceMessageEnvelope,
  IStreamAudioPayload,
  ILowBalancePayload,
  ICreateCallResponse,
  ICreateCallRequest,
  ICall
} from '../../interfaces/voice-call.interfaces';

import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class VoiceCallService {
  private ws!: WebSocket;
  private readonly baseUrl = `${environment.apiCallBaseUrl}/calls`;

  private readonly _callStarted$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly _callEnded$: Subject<void> = new Subject<void>();
  private readonly _agentAudio$: Subject<string> = new Subject<string>();
  private readonly _lowBalance$: Subject<number> = new Subject<number>();
  private sessionToken: string;

  /** ---- Agent audio buffering ---- */
  private agentAudioBuffer: string[] = [];
  private agentAudioTimeout: ReturnType<typeof setTimeout>;

  constructor(
    private readonly ngZone: NgZone,
    private readonly http: HttpClient
  ) {}

  /** -------------------- REST API METHODS -------------------- **/

  /**
   * Create a new call session between a user and a creator
   */
  createCall(payload: ICreateCallRequest): Observable<ICreateCallResponse> {
    this.sessionToken = localStorage.getItem('accessToken');
    return this.http.post<ICreateCallResponse>(this.baseUrl, payload);
  }

  /**
   * Retrieve a call by ID
   */
  getCall(id: string): Observable<ICall> {
    return this.http.get<ICall>(`${this.baseUrl}/${id}`);
  }

  /**
   * Establish a WebSocket data stream for the specific call
   * The backend upgrades this HTTP endpoint to WS (`/calls/{id}/data`)
   */
  connectToCallStream(callId: string): void {
    const wsUrl =
      this.baseUrl.replace(/^https?/, 'wss') +
      `/${callId}/data/?token=${encodeURIComponent(this.sessionToken)}`;
    this.connect(wsUrl);
  }

  /** -------------------- WEBSOCKET CONNECTION -------------------- **/

  /** Internal connect method */
  private connect(url: string): void {
    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      this.startCall();
      console.log('[VoiceCall] Connected to WebSocket');
    };

    this.ws.onmessage = (event: MessageEvent<string>) => {
      this.ngZone.run(() => this.handleMessage(event.data));
    };

    this.ws.onclose = () => {
      console.log('[VoiceCall] WebSocket closed');
      this._callStarted$.next(false);
    };

    this.ws.onerror = error => console.error('[VoiceCall] WebSocket error', error);
  }

  /** Disconnect WS */
  disconnect(): void {
    if (this.ws) this.ws.close();
  }

  /** Handle incoming messages */
  private handleMessage(rawData: string): void {
    let message: IVoiceMessageEnvelope<unknown>;
    try {
      message = JSON.parse(rawData);
    } catch {
      console.error('[VoiceCall] Invalid WS message', rawData);
      return;
    }

    switch (message.type) {
      case 'CallStarted':
        this._callStarted$.next(true);
        break;
      case 'CallEnded':
        this._callStarted$.next(false);
        this._callEnded$.next();
        break;
      case 'AgentAudio':
        if (message.payload && typeof message.payload === 'object' && 'data' in message.payload) {
          this.bufferAgentAudio((message.payload as IStreamAudioPayload).data);
        }
        break;
      case 'LowBalance':
        if (message.payload && typeof message.payload === 'object' && 'amount' in message.payload) {
          this._lowBalance$.next(parseFloat((message.payload as ILowBalancePayload).amount));
        }
        break;
      default:
        console.warn('[VoiceCall] Unknown message type:', message.type);
    }
  }

  /** Send WS messages */
  private sendMessage<T>(type: string, payload?: T | null): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const msg: IVoiceMessageEnvelope<T> = { type, payload: payload ?? null };
      this.ws.send(JSON.stringify(msg));
    } else {
      console.error('[VoiceCall] WebSocket is not open. Cannot send:', type);
    }
  }

  /** -------------------- AGENT AUDIO BUFFERING & PLAYBACK -------------------- **/
  private bufferAgentAudio(base64Chunk: string) {
    this.agentAudioBuffer.push(base64Chunk);

    // Clear previous timeout
    if (this.agentAudioTimeout) clearTimeout(this.agentAudioTimeout);

    // Wait for 500ms after the last chunk to consider the audio "complete"
    this.agentAudioTimeout = setTimeout(() => {
      const fullBase64Audio = this.agentAudioBuffer.join('');
      this._agentAudio$.next(fullBase64Audio);

      // Reset buffer
      this.agentAudioBuffer = [];
    }, 500);
  }

  /** -------------------- PUBLIC API -------------------- **/

  /** Observables */
  get callStarted$(): Observable<boolean> {
    return this._callStarted$.asObservable();
  }

  get callEnded$(): Observable<void> {
    return this._callEnded$.asObservable();
  }

  get agentAudio$(): Observable<string> {
    return this._agentAudio$.asObservable();
  }

  get lowBalance$(): Observable<number> {
    return this._lowBalance$.asObservable();
  }

  /** Call control */
  startCall(): void {
    this.sendMessage('StartCall', {});
  }

  endCall(): void {
    this.sendMessage('EndCall', {});
  }

  streamAudio(base64Chunk: string): void {
    this.sendMessage<IStreamAudioPayload>('StreamAudio', { data: base64Chunk });
  }
}
